/**
 * FilterPresetManager Component
 * Manage saved filter presets - save, load, delete
 */

import React, { useState } from 'react';
import { Save, Star, Trash2 } from 'lucide-react';
import { useFilterPresets, useCreateFilterPreset, useUpdateFilterPreset, useDeleteFilterPreset } from '../../hooks';
import type { OKRFilters, FilterPreset } from '../../types/okr';
import { toast } from 'react-hot-toast';

interface FilterPresetManagerProps {
  currentFilters: OKRFilters;
  onLoadPreset: (filters: OKRFilters) => void;
}

export const FilterPresetManager: React.FC<FilterPresetManagerProps> = ({
  currentFilters,
  onLoadPreset,
}) => {
  const [isSaveMode, setIsSaveMode] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const { data: presets, isLoading } = useFilterPresets();
  const createPresetMutation = useCreateFilterPreset();
  const updatePresetMutation = useUpdateFilterPreset();
  const deletePresetMutation = useDeleteFilterPreset();

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    try {
      await createPresetMutation.mutateAsync({
        name: presetName.trim(),
        description: presetDescription.trim() || undefined,
        filters: currentFilters,
        isPublic,
      });

      toast.success('Filter preset saved successfully');
      setIsSaveMode(false);
      setPresetName('');
      setPresetDescription('');
      setIsPublic(false);
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to save preset');
    }
  };

  const handleLoadPreset = (preset: FilterPreset) => {
    onLoadPreset(preset.filters);
    toast.success(`Loaded preset: ${preset.name}`);
  };

  const handleSetDefault = async (preset: FilterPreset) => {
    try {
      await updatePresetMutation.mutateAsync({
        id: preset.id,
        data: { isDefault: !preset.isDefault },
      });

      toast.success(preset.isDefault ? 'Default preset removed' : 'Default preset set');
    } catch {
      toast.error('Failed to update preset');
    }
  };

  const handleDeletePreset = async (preset: FilterPreset) => {
    if (!window.confirm(`Are you sure you want to delete "${preset.name}"?`)) {
      return;
    }

    try {
      await deletePresetMutation.mutateAsync(preset.id);
      toast.success('Preset deleted successfully');
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to delete preset');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-stone-700">Saved Presets</h3>
        {!isSaveMode && (
          <button
            onClick={() => setIsSaveMode(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Current
          </button>
        )}
      </div>

      {/* Save Form */}
      {isSaveMode && (
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg space-y-3">
          <input
            type="text"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder="Preset name"
            className="block w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            autoFocus
          />

          <input
            type="text"
            value={presetDescription}
            onChange={(e) => setPresetDescription(e.target.value)}
            placeholder="Description (optional)"
            className="block w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />

          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-2 focus:ring-primary-500"
            />
            Make public (visible to all users in organization)
          </label>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSavePreset}
              disabled={createPresetMutation.isPending}
              className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50"
            >
              {createPresetMutation.isPending ? 'Saving...' : 'Save Preset'}
            </button>
            <button
              onClick={() => {
                setIsSaveMode(false);
                setPresetName('');
                setPresetDescription('');
                setIsPublic(false);
              }}
              className="px-3 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Presets List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-center py-4 text-sm text-stone-500">Loading presets...</div>
        ) : presets && presets.length > 0 ? (
          presets.map((preset) => (
            <div
              key={preset.id}
              className="flex items-center gap-2 p-3 bg-white border border-stone-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <button
                onClick={() => handleLoadPreset(preset)}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-stone-900">{preset.name}</span>
                  {preset.isDefault && (
                    <span className="px-2 py-0.5 bg-warning-100 text-warning-700 text-xs font-medium rounded">
                      Default
                    </span>
                  )}
                  {preset.isPublic && (
                    <span className="px-2 py-0.5 bg-success-100 text-success-700 text-xs font-medium rounded">
                      Public
                    </span>
                  )}
                </div>
                {preset.description && (
                  <p className="text-xs text-stone-500 mt-1">{preset.description}</p>
                )}
              </button>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleSetDefault(preset)}
                  className={`p-1.5 rounded hover:bg-stone-100 transition-colors ${
                    preset.isDefault ? 'text-warning-600' : 'text-stone-400'
                  }`}
                  title={preset.isDefault ? 'Remove as default' : 'Set as default'}
                >
                  <Star className="w-4 h-4" fill={preset.isDefault ? 'currentColor' : 'none'} />
                </button>

                <button
                  onClick={() => handleDeletePreset(preset)}
                  className="p-1.5 text-danger-600 hover:bg-danger-50 rounded transition-colors"
                  title="Delete preset"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6 text-sm text-stone-500">
            No saved presets yet. Save your current filters to create a preset!
          </div>
        )}
      </div>
    </div>
  );
};
